import React, { useState } from 'react';
import type {
  MetaSpecialAdCategory,
  MetaTargetingSpec,
} from '@/services/api/metaAds';
import { GeoLocationSection } from './GeoLocationSection';
import { DemographicsSection } from './DemographicsSection';
import { InterestsSection } from './InterestsSection';
import { PlacementsSection } from './PlacementsSection';
import { DevicesSection } from './DevicesSection';
import { LanguagesSection } from './LanguagesSection';

interface TargetingStepProps {
  /** Pre-loaded parent campaign — used to enforce age 18+ for regulated
   *  special_ad_categories. */
  parentSpecialAdCategories: MetaSpecialAdCategory[];
  /** Used by InterestsSection to bind its React Query to the right
   *  ad account for the proxy call. */
  adAccountId: string;
  targeting: MetaTargetingSpec;
  /** React Dispatch shape so `update()` below can use the functional
   *  setter form. Two-call sequences inside child sections (e.g.
   *  PlacementsSection.togglePlatform clearing positions after removing
   *  a platform) need the second call to see the first's effect; a
   *  value-only setter would have them both spread the same stale
   *  `targeting` prop and the second would clobber the first. */
  onChangeTargeting: React.Dispatch<React.SetStateAction<MetaTargetingSpec>>;
  errors?: {
    countries?: string;
    age?: string;
    placements?: string;
  };
}

type SectionKey =
  | 'geo'
  | 'demographics'
  | 'interests'
  | 'placements'
  | 'devices'
  | 'languages';

const SECTION_LABELS: Record<SectionKey, string> = {
  geo: 'Location',
  demographics: 'Age & Gender',
  interests: 'Interests',
  placements: 'Placements',
  devices: 'Devices',
  languages: 'Languages',
};

const SECTION_SUMMARIES: Partial<
  Record<SectionKey, (t: MetaTargetingSpec) => string>
> = {
  geo: (t) =>
    t.geo_locations.countries.length > 0
      ? t.geo_locations.countries.join(', ')
      : 'No countries',
  demographics: (t) => `Ages ${t.age_min}–${t.age_max}, ${t.genders.length === 2 ? 'all genders' : t.genders[0] === 'MALE' ? 'men' : 'women'}`,
  interests: (t) =>
    t.interests.length === 0
      ? 'None'
      : `${t.interests.length} selected`,
  placements: (t) =>
    // Empty publisher_platforms is Meta's wire convention for Advantage+
    // Placements regardless of the flag — labeling it "Manual" would
    // mislead the user about what Meta will actually do. See
    // CreateAdSetWizard.tsx defaultTargeting() for the full reasoning.
    t.advantage_placements || t.publisher_platforms.length === 0
      ? 'Advantage+'
      : `Manual (${t.publisher_platforms.join(', ')})`,
  devices: (t) =>
    t.device_platforms.length === 0 || t.device_platforms.length === 2
      ? 'All devices'
      : t.device_platforms[0] === 'mobile' ? 'Mobile only' : 'Desktop only',
  languages: (t) =>
    t.locales.length === 0 ? 'No restriction' : `${t.locales.length} selected`,
};

/**
 * Targeting — the meaty step. 6 collapsible sub-sections, two open by
 * default (Geo + Demographics), the rest collapsed. Each section is a
 * standalone component under `./targeting/`.
 *
 * Single-step layout (per the user's plan-mode choice) rather than
 * splitting across multiple wizard steps. This matches Meta's own UI
 * where the audience builder is one continuous form.
 */
export const TargetingStep: React.FC<TargetingStepProps> = ({
  parentSpecialAdCategories,
  adAccountId,
  targeting,
  onChangeTargeting,
  errors,
}) => {
  const [openSections, setOpenSections] = useState<Set<SectionKey>>(
    new Set(['geo', 'demographics']),
  );

  const toggle = (key: SectionKey) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const update = (patch: Partial<MetaTargetingSpec>) => {
    // Functional form so back-to-back update() calls in the same event
    // handler (e.g. PlacementsSection.togglePlatform's
    // publisher_platforms + facebook_positions sequence) each see the
    // previous call's effect. The value-only form spread the same
    // closure-captured `targeting` prop in both calls, so the second
    // clobbered the first.
    onChangeTargeting((prev) => ({ ...prev, ...patch }));
  };

  const renderSection = (
    key: SectionKey,
    body: React.ReactNode,
    badge?: string,
  ) => {
    const isOpen = openSections.has(key);
    return (
      <div className="border border-gray-200 rounded-lg">
        <button
          type="button"
          onClick={() => toggle(key)}
          aria-expanded={isOpen}
          aria-controls={`meta-targeting-section-${key}`}
          className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-50 focus:outline-none focus:bg-blue-50"
        >
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-900">
              {SECTION_LABELS[key]}
            </span>
            {badge && (
              <span className="text-xs text-gray-500">{badge}</span>
            )}
          </div>
          <span
            aria-hidden="true"
            className={`text-gray-400 transition-transform ${
              isOpen ? 'rotate-90' : ''
            }`}
          >
            ›
          </span>
        </button>
        {isOpen && (
          <div
            id={`meta-targeting-section-${key}`}
            className="p-4 border-t border-gray-200"
          >
            {body}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Targeting
        </h3>
        <p className="text-sm text-gray-600">
          Define the audience. Location is required; everything else is
          optional and broadens delivery when left at defaults.
        </p>
      </div>

      {/* A4.3.2.5: Optimization mode — Advantage Audience opt-in.
          Advantage+ Placements lives inside PlacementsSection below
          because it shares structure with the manual placement UI;
          keeping the two Meta toggles labeled distinctly avoids
          conflating two different Meta features. */}
      <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
        <p className="text-sm font-medium text-gray-900 mb-2">
          Optimization mode
        </p>
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={targeting.advantage_audience}
            onChange={(e) =>
              update({ advantage_audience: e.target.checked })
            }
            className="mt-0.5"
            aria-describedby="advantage-audience-help"
          />
          <span className="text-sm text-gray-700">
            <span className="font-medium">Use Advantage Audience</span>
            <span
              id="advantage-audience-help"
              className="block text-xs text-gray-500"
            >
              Meta&apos;s AI may serve to users outside your manual
              targeting below when it predicts higher conversion
              likelihood. Recommended when you&apos;re unsure of the
              exact audience.
            </span>
          </span>
        </label>
      </div>

      {renderSection(
        'geo',
        <GeoLocationSection
          countries={targeting.geo_locations.countries}
          onChange={(countries) =>
            update({ geo_locations: { countries } })
          }
          error={errors?.countries}
        />,
        SECTION_SUMMARIES.geo?.(targeting),
      )}

      {renderSection(
        'demographics',
        <DemographicsSection
          ageMin={targeting.age_min}
          ageMax={targeting.age_max}
          genders={targeting.genders}
          parentSpecialAdCategories={parentSpecialAdCategories}
          onChangeAgeMin={(age_min) => update({ age_min })}
          onChangeAgeMax={(age_max) => update({ age_max })}
          onChangeGenders={(genders) => update({ genders })}
          error={errors?.age}
        />,
        SECTION_SUMMARIES.demographics?.(targeting),
      )}

      {renderSection(
        'interests',
        <InterestsSection
          adAccountId={adAccountId}
          interests={targeting.interests}
          onChange={(interests) => update({ interests })}
        />,
        SECTION_SUMMARIES.interests?.(targeting),
      )}

      {renderSection(
        'placements',
        <PlacementsSection
          publisherPlatforms={targeting.publisher_platforms}
          facebookPositions={targeting.facebook_positions}
          instagramPositions={targeting.instagram_positions}
          advantagePlacements={targeting.advantage_placements}
          onChangePublisherPlatforms={(publisher_platforms) =>
            update({ publisher_platforms })
          }
          onChangeFacebookPositions={(facebook_positions) =>
            update({ facebook_positions })
          }
          onChangeInstagramPositions={(instagram_positions) =>
            update({ instagram_positions })
          }
          onSetPlacementMode={(mode) =>
            update(
              mode === 'advantage'
                ? {
                    advantage_placements: true,
                    publisher_platforms: [],
                    facebook_positions: [],
                    instagram_positions: [],
                  }
                : {
                    advantage_placements: false,
                    // Default manual selection: facebook + instagram (the
                    // two most common). Mirrors the prior in-section
                    // default; keeps the user from facing an empty
                    // manual UI after switching off Advantage+.
                    publisher_platforms:
                      targeting.publisher_platforms.length === 0
                        ? ['facebook', 'instagram']
                        : targeting.publisher_platforms,
                  },
            )
          }
          error={errors?.placements}
        />,
        SECTION_SUMMARIES.placements?.(targeting),
      )}

      {renderSection(
        'devices',
        <DevicesSection
          devicePlatforms={targeting.device_platforms}
          onChange={(device_platforms) => update({ device_platforms })}
        />,
        SECTION_SUMMARIES.devices?.(targeting),
      )}

      {renderSection(
        'languages',
        <LanguagesSection
          locales={targeting.locales}
          onChange={(locales) => update({ locales })}
        />,
        SECTION_SUMMARIES.languages?.(targeting),
      )}
    </div>
  );
};
